import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

export interface MailOpenInfo {
  openCount: number;
  lastOpenedAt: string | null;
}

/**
 * Stato di apertura (tracking) per un insieme di messaggi inviati, chiave = message_id
 * (provider_message_id lato mail_tracking_messages). Ritorna solo i messaggi con almeno
 * un'apertura REALE (open_count > 0; i prefetch MPP non incrementano open_count).
 * RLS limita alla propria organizzazione.
 */
export function useMailOpenStatus(messageIds: string[]): Map<string, MailOpenInfo> {
  const [map, setMap] = useState<Map<string, MailOpenInfo>>(new Map());
  // Chiave stabile per l'effetto (evita refetch se l'array cambia solo riferimento).
  const key = messageIds.filter(Boolean).sort().join(',');

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setMap(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('mail_tracking_messages')
        .select('provider_message_id, open_count, last_opened_at')
        .in('provider_message_id', ids)
        .gt('open_count', 0);
      if (cancelled || error || !data) return;
      const next = new Map<string, MailOpenInfo>();
      for (const row of data) {
        if (row.provider_message_id) {
          next.set(row.provider_message_id, {
            openCount: row.open_count ?? 0,
            lastOpenedAt: row.last_opened_at ?? null,
          });
        }
      }
      setMap(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  return map;
}

/**
 * Stato di apertura aggregato per THREAD (somma open_count dei messaggi tracciati
 * del thread, max last_opened_at). Chiave = thread_id (Gmail threadId / Outlook
 * conversationId). Usato dalla lista thread e dalle note email dei lead, che
 * conoscono solo il thread e non i singoli message id.
 */
export function useMailOpenStatusByThread(threadIds: string[]): Map<string, MailOpenInfo> {
  const [map, setMap] = useState<Map<string, MailOpenInfo>>(new Map());
  const key = threadIds.filter(Boolean).sort().join(',');

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setMap(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('mail_tracking_messages')
        .select('thread_id, open_count, last_opened_at')
        .in('thread_id', ids)
        .gt('open_count', 0);
      if (cancelled || error || !data) return;
      const next = new Map<string, MailOpenInfo>();
      for (const row of data) {
        if (!row.thread_id) continue;
        const prev = next.get(row.thread_id);
        next.set(row.thread_id, {
          openCount: (prev?.openCount ?? 0) + (row.open_count ?? 0),
          lastOpenedAt:
            !prev?.lastOpenedAt || (row.last_opened_at && row.last_opened_at > prev.lastOpenedAt)
              ? row.last_opened_at ?? prev?.lastOpenedAt ?? null
              : prev.lastOpenedAt,
        });
      }
      setMap(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  return map;
}
